export type HelpChapter = {
  id:string;
  title:string;
  lead:string;
  sections:Array<{ title:string; paragraphs?:string[]; items?:string[] }>;
};

export const helpChapters:HelpChapter[] = [
  { id:'quick-start', title:'快速开始', lead:'用几分钟熟悉 Mizuki Light Studio 的基本工作流程。', sections:[
    { title:'视口操作', items:['按住鼠标左键旋转视角，右键平移，滚轮缩放。','使用顶部“控制台”管理灯光、渲染和姿势；使用“参考物”打开独立道具窗口。','控制台与参考物窗口互斥，但控制台打开时 3D 工作区仍可操作。'] },
    { title:'推荐流程', items:['先选择角色与构图机位。','调整姿势和参考物关系。','切换灰模观察明暗分界，再完成灯光和渲染参数。','最后隐藏界面并导出 PNG。'] },
  ]},
  { id:'lighting', title:'灯光系统', lead:'使用预设快速起步，再精调每一盏灯。', sections:[
    { title:'灯光类型', items:['平行光适合模拟太阳或稳定主光，可用 X/Y/Z 旋转改变方向。','点光源从一个位置向四周发光，适合补光、轮廓光和局部气氛光。','环境光控制场景整体基础亮度。'] },
    { title:'编辑方法', items:['在灯光列表选择光源后，可调整名称、颜色、强度和距离。','视口中的彩色标记可以直接拖动。','灯光标记只用于编辑，不会出现在导出的画面中。'] },
  ]},
  { id:'rendering', title:'渲染与灰模', lead:'在完整材质和中性灰模之间切换，专注观察光影。', sections:[
    { title:'灰模', items:['灰模会临时替换角色材质，但不会删除原贴图。','适合观察体积、投影、明暗分界线和轮廓。','再次选择完整材质即可恢复角色外观。'] },
    { title:'渲染参数', items:['饱和度控制整体色彩浓度。','自发光与眼睛自发光控制暗部可见度。','Ramp 范围和过渡宽度决定卡通阴影边界。','轮廓宽度、Bloom 和脸颊边缘光用于强化风格。'] },
  ]},
  { id:'posing', title:'姿势系统', lead:'像关节人偶一样调整身体与头发骨骼。', sections:[
    { title:'调整姿势', items:['进入姿势模式后，在分组列表中选择关节。','使用视口旋转环，或使用 X/Y/Z 滑块精确调整。','身体和头发骨骼都可以单独复位，也可以立即恢复默认姿态。'] },
    { title:'姿势数据', items:['下载 JSON：保存为本地姿势文件。','复制 JSON：方便发送、记录或加入预设。','复制分享 URL：其他人打开网址后自动还原姿势。','导入前会校验格式和角色，无法匹配的关节会被忽略。'] },
  ]},
  { id:'reference', title:'参考物', lead:'用简单几何体搭建比例、透视、遮挡和投影关系。', sections:[
    { title:'可用形状', items:['立方体、球体、圆柱、三棱柱。','楼梯支持自定义总长、总宽、总高和 1～50 阶。','当前默认最多创建 20 个参考物。'] },
    { title:'编辑与选择', items:['在参考物列表选择对象，或双击视口中的参考物。','可修改名称、尺寸、颜色、透明度、线框、显示和阴影。','W 移动、E 旋转、R 缩放；Ctrl+D 复制；Delete 删除。','每个参考物拥有稳定 ID，重命名或排序不会改变 ID。'] },
  ]},
  { id:'history', title:'撤销与恢复', lead:'历史记录覆盖姿势、灯光、色彩、渲染参数和参考物。', sections:[
    { title:'历史规则', items:['Ctrl+Z 撤销上一步。','Ctrl+Shift+Z 恢复刚刚撤销的操作。','滑块连续拖动会合并为一步。','撤销后进行新编辑会清空恢复记录，避免产生分叉歧义。','切换角色会清空历史，防止状态应用到错误模型。'] },
  ]},
  { id:'shortcuts', title:'快捷键', lead:'快捷键仅在没有编辑文字或打开帮助窗口时生效。', sections:[
    { title:'常用快捷键', items:['W：移动参考物','E：旋转参考物','R：缩放参考物','Ctrl+D：复制参考物','Delete：删除参考物','Ctrl+Z：撤销','Ctrl+Shift+Z：恢复','H：隐藏或显示界面','Esc：关闭当前窗口'] },
  ]},
  { id:'export', title:'导出与分享', lead:'导出画面，或把姿势作为可复用数据分享。', sections:[
    { title:'PNG 画面', items:['“导出画面”和“导出截图”都会保存当前视口。','变换控制环和灯光标记不会写入图片。','隐藏界面模式适合在导出前检查最终构图。'] },
    { title:'当前数据范围', items:['姿势 JSON 和分享 URL 只包含角色及发生变化的关节。','完整灯光、参考物和场景参数尚未加入姿势文件。','复杂姿势推荐使用 JSON，避免分享 URL 过长。'] },
  ]},
  { id:'faq', title:'常见问题', lead:'常见现象通常可以通过以下方法快速处理。', sections:[
    { title:'加载与显示', items:['首次加载较慢：FBX 模型和贴图需要下载并解析，请等待进度完成。','看不到控制环：确认已选择灯光、关节或参考物，并检查是否处于正确模式。','按钮不可用：当前角色可能没有对应骨骼，或模型仍在加载。'] },
    { title:'部署与缓存', items:['公网固定地址是 mizuki-light-studio.pages.dev。','若部署后仍显示旧界面，使用 Ctrl+F5 强制刷新。','项目正式部署只使用既有的 mizuki-light-studio Cloudflare Pages 项目。'] },
  ]},
];
